import { useState } from 'react';
import {
  recomputeRefinementSession,
  failRefinementSession,
  escalateRefinementSession,
  cancelRefinementSession,
} from '../../api/client';
import type { RefinementSession } from '../../api/types';

interface Props {
  session: RefinementSession;
  onAfterAction?: (next: RefinementSession | null) => void;
}

type ActionKey = 'recompute' | 'fail' | 'escalate' | 'cancel';

const CONFIRMATIONS: Record<ActionKey, string> = {
  recompute: 'Re-score this pair and advance the state machine?',
  fail: 'Force-fail this refinement session? Both pebbles will get a doorbell.',
  escalate: 'Bump this session straight to consent? Both pebbles will get a doorbell.',
  cancel: 'Soft-cancel this session? No doorbell will be sent.',
};

export default function RefinementActionBar({ session, onAfterAction }: Props) {
  const [pending, setPending] = useState<ActionKey | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function run(action: ActionKey) {
    if (!confirm(CONFIRMATIONS[action])) return;
    setPending(action);
    setError(null);
    try {
      const result = await {
        recompute: () => recomputeRefinementSession(session.id),
        fail: () => failRefinementSession(session.id),
        escalate: () => escalateRefinementSession(session.id),
        cancel: () => cancelRefinementSession(session.id),
      }[action]();
      onAfterAction?.(result.session);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setPending(null);
    }
  }

  const isTerminal =
    session.status === 'failed' ||
    session.status === 'escalated_consent' ||
    session.status === 'cancelled';

  return (
    <div className="card">
      <div className="text-xs uppercase text-gray-500 mb-2">Operator actions</div>
      <div className="flex flex-wrap gap-2">
        <ActionButton
          label="Recompute now"
          onClick={() => run('recompute')}
          pending={pending === 'recompute'}
          disabled={isTerminal || pending !== null}
        />
        <ActionButton
          label="Force fail"
          tone="danger"
          onClick={() => run('fail')}
          pending={pending === 'fail'}
          disabled={isTerminal || pending !== null}
        />
        <ActionButton
          label="Bump to consent"
          tone="success"
          onClick={() => run('escalate')}
          pending={pending === 'escalate'}
          disabled={isTerminal || pending !== null}
        />
        <ActionButton
          label="Cancel"
          tone="muted"
          onClick={() => run('cancel')}
          pending={pending === 'cancel'}
          disabled={isTerminal || pending !== null}
        />
      </div>
      {isTerminal && (
        <div className="text-xs text-gray-500 mt-2 italic">
          Session is in terminal state ({session.status}); operator actions are disabled.
        </div>
      )}
      {error && (
        <div className="mt-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
          {error}
        </div>
      )}
    </div>
  );
}

function ActionButton({
  label,
  onClick,
  pending,
  disabled,
  tone = 'primary',
}: {
  label: string;
  onClick: () => void;
  pending: boolean;
  disabled: boolean;
  tone?: 'primary' | 'danger' | 'success' | 'muted';
}) {
  const cls = {
    primary: 'bg-pebbl-600 text-white hover:bg-pebbl-700',
    danger: 'bg-red-600 text-white hover:bg-red-700',
    success: 'bg-green-600 text-white hover:bg-green-700',
    muted: 'bg-gray-200 text-gray-800 hover:bg-gray-300',
  }[tone];
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${cls}`}
    >
      {pending ? '…' : label}
    </button>
  );
}
